import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import Lenis from "lenis";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ChevronDown } from "lucide-react";
import { DuoClairLogo } from "@/components/DuoClairLogo";
import { SiteMenu } from "@/components/SiteMenu";

const CATEGORIES = [
  {
    slug: "social-identita",
    title: "Social & Identità",
    short: "Gestione dei profili social, miglioramento dell'immagine digitale, restyling dei profili, creazione di grafiche e identità visiva.",
  },
  {
    slug: "video-contenuti-ai",
    title: "Video & Contenuti AI",
    short: "Produzione di video promozionali e contenuti creativi realizzati con intelligenza artificiale, singolarmente oppure attraverso piani mensili.",
  },
  {
    slug: "siti-web-automazioni",
    title: "Siti Web & Automazioni",
    short: "Creazione di siti vetrina professionali, pagine digitali e strumenti per presentare l'attività, raccogliere richieste e facilitare il contatto con i clienti.",
  },
  {
    slug: "recensioni-qr-nfc",
    title: "Recensioni & QR/NFC",
    short: "Espositori QR/NFC, card personalizzate e adesivi per facilitare l'accesso alle recensioni Google e ad altre destinazioni digitali concordate.",
  },
  {
    slug: "gestione-completa",
    title: "Gestione Digitale Completa",
    short: "Soluzioni mensili coordinate che comprendono gestione social, Reel, video AI, attività promozionali e Brand Kit di benvenuto personalizzati.",
  },
];

export default function ServiziPage() {
  const lenisRef = useRef(null);
  const [openSlug, setOpenSlug] = useState(null);

  useEffect(() => {
    const lenis = new Lenis({ duration: 1.1, smoothWheel: true });
    lenisRef.current = lenis;
    let raf;
    const loop = (t) => {
      lenis.raf(t);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    window.scrollTo(0, 0);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
    };
  }, []);

  return (
    <div className="min-h-screen text-foreground overflow-x-clip" data-testid="servizi-page">
      <header className="fixed top-0 inset-x-0 z-50 bg-black/40 backdrop-blur-md border-b border-white/5">
        <div className="max-w-3xl mx-auto px-5 h-14 sm:h-16 flex items-center justify-between">
          <DuoClairLogo />
          <div className="flex items-center gap-3">
            <Link
              to="/"
              state={{ restoreScroll: true }}
              data-testid="servizi-back-link"
              className="hidden sm:inline-flex items-center gap-2 text-xs text-zinc-300 hover:text-gold-light transition-colors"
            >
              <ArrowLeft className="w-3.5 h-3.5" /> Torna alla home
            </Link>
            <SiteMenu />
          </div>
        </div>
      </header>

      <div className="relative z-10 pt-24 sm:pt-28 pb-12 px-5">
        <div className="max-w-xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="text-center mb-9"
          >
            <p className="eyebrow mb-3">Le soluzioni</p>
            <h1 className="font-display headline-3d text-3xl sm:text-4xl tracking-tight" data-testid="servizi-title">
              <span className="chrome-text">Servizi </span>
              <span className="gold-text">DuoClair</span>
            </h1>
            <p className="mt-3 text-[13px] sm:text-sm text-zinc-300 leading-relaxed max-w-md mx-auto">
              Cinque aree per rendere la tua attività più forte online. Tocca una categoria per scoprirla.
            </p>
          </motion.div>

          <div className="space-y-2.5" data-testid="services-list">
            {CATEGORIES.map((s, i) => {
              const open = openSlug === s.slug;
              return (
                <motion.div
                  key={s.slug}
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.08 + i * 0.05 }}
                  className={`bg-[#0D0D10]/70 border rounded-2xl overflow-hidden transition-colors duration-300 ${open ? "border-gold/40" : "border-white/10"}`}
                  data-testid={`service-item-${s.slug}`}
                >
                  <button
                    onClick={() => setOpenSlug(open ? null : s.slug)}
                    aria-expanded={open}
                    aria-controls={`service-panel-${s.slug}`}
                    data-testid={`service-toggle-${s.slug}`}
                    className="w-full flex items-center gap-3.5 p-4 sm:p-5 text-left"
                  >
                    <span className="font-mono text-[10px] text-gold/80 border border-gold/30 rounded-full w-7 h-7 flex items-center justify-center shrink-0">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <span className="flex-1 min-w-0">
                      <span className="block font-sans font-bold text-slate-100 text-sm sm:text-base uppercase tracking-wide leading-tight">
                        {s.title}
                      </span>
                    </span>
                    <ChevronDown
                      className={`w-4 h-4 text-gold-light shrink-0 transition-transform duration-300 ${open ? "rotate-180" : ""}`}
                    />
                  </button>
                  <AnimatePresence initial={false}>
                    {open && (
                      <motion.div
                        id={`service-panel-${s.slug}`}
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                      >
                        <div className="px-4 sm:px-5 pb-5 pt-3 border-t border-white/5">
                          <p className="text-xs sm:text-[13px] text-zinc-300 leading-relaxed">{s.short}</p>
                          <p className="mt-3 text-[11px] sm:text-xs text-gold/80 leading-relaxed">
                            Pacchetti, quantità e prezzi sono disponibili nel listino riservato DuoClair,
                            accessibile dopo l'analisi gratuita.
                          </p>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              );
            })}
          </div>

          {/* Final CTA — unlock via existing analysis form */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.7 }}
            className="relative mt-10 bg-[#0D0D10]/75 border border-white/10 rounded-2xl px-5 py-9 sm:px-10 text-center overflow-hidden"
            data-testid="servizi-final-cta"
          >
            <div className="absolute inset-x-10 top-0 gold-hairline" />
            <h2 className="font-display headline-3d text-xl sm:text-2xl tracking-tight leading-snug">
              <span className="chrome-text">Vuoi scoprire il piano più adatto </span>
              <span className="gold-text">alla tua attività?</span>
            </h2>
            <p className="mt-3 text-[13px] sm:text-sm text-zinc-300 leading-relaxed max-w-sm mx-auto">
              Richiedi un'analisi gratuita e sblocca il nostro listino riservato. Scopri le offerte
              DuoClair, confronta i servizi e configura la soluzione più adatta al tuo negozio.
            </p>
            <Link
              to="/"
              state={{ openForm: true }}
              data-testid="servizi-analysis-cta"
              className="btn-champagne inline-block mt-6 rounded-full px-8 sm:px-10 py-3.5 font-bold text-sm sm:text-base tracking-tight hover:scale-[1.03] active:scale-[0.98] transition-transform duration-300"
            >
              Richiedi l'analisi e scopri le offerte
            </Link>
          </motion.div>
        </div>
      </div>
    </div>
  );
}
